import DOMPurify from "dompurify";
import { environment } from "src/config/environment";

/**
 * Sanitization configuration for API response security
 * Controls how DOMPurify sanitizes responses per API endpoint
 */

// ─── Enums ───────────────────────────────────────────────────────────────────

export enum SanitizationModeType {
	/** Default: strip ALL HTML tags, keep text content */
	Default = "default",
	/** Skip sanitization entirely for this endpoint */
	Skip = "skip",
	/** Sanitize all fields EXCEPT specified keys (recursive key match) */
	Partial = "partial",
	/** Allow specific HTML tags (for rich text APIs) */
	AllowTags = "allowTags",
}

// ─── Interfaces ──────────────────────────────────────────────────────────────

interface ISanitizationBaseConfig {
	mode: SanitizationModeType;
	/** Optional comment explaining WHY this override exists */
	reason?: string;
}

interface ISanitizationSkipConfig extends ISanitizationBaseConfig {
	mode: SanitizationModeType.Skip;
}

interface ISanitizationPartialConfig extends ISanitizationBaseConfig {
	mode: SanitizationModeType.Partial;
	/** Field keys to skip sanitization for (matched recursively at any depth) */
	skipFields: string[];
}

interface ISanitizationAllowTagsConfig extends ISanitizationBaseConfig {
	mode: SanitizationModeType.AllowTags;
	allowedTags: string[];
	allowedAttr?: string[];
}

export type ISanitizationConfig = ISanitizationSkipConfig | ISanitizationPartialConfig | ISanitizationAllowTagsConfig;

// ─── Config Resolver ─────────────────────────────────────────────────────────

/**
 * Extracts pathname from API URL, stripping base URL and query params.
 * Handles both full URLs and relative paths.
 */
const extractPathname = (url: string): string => {
	try {
		// Remove base URL if present
		const baseUrl = environment.REACT_APP_API_URL || "";
		const cleanUrl = url.startsWith(baseUrl) ? url.slice(baseUrl.length) : url;

		// Remove query params
		const [pathname] = cleanUrl.split("?");

		// Normalize: ensure leading slash, remove trailing slash
		return pathname.startsWith("/") ? pathname.replace(/\/+$/, "") : `/${pathname}`.replace(/\/+$/, "");
	} catch {
		return url;
	}
};

/**
 * Resolves sanitization config for a given API URL.
 * Returns undefined if no override exists (use default sanitization).
 */
export const getSanitizationConfig = (url: string): ISanitizationConfig | undefined => {
	const pathname = extractPathname(url);

	// 1. Exact match
	if (API_SANITIZATION_MAP[pathname]) {
		return API_SANITIZATION_MAP[pathname];
	}

	// 2. Prefix/wildcard match (entries ending with *)
	const wildcardMatch = Object.keys(API_SANITIZATION_MAP).find((pattern) => {
		if (!pattern.endsWith("*")) return false;
		const prefix = pattern.slice(0, -1); // Remove trailing *
		return pathname.startsWith(prefix);
	});

	if (wildcardMatch) {
		return API_SANITIZATION_MAP[wildcardMatch];
	}

	return undefined;
};

// ─── Core Sanitization Logic ─────────────────────────────────────────────────

/**
 * Sanitize a single string value using DOMPurify.
 * Default: strip ALL HTML, keep text content only.
 *
 * In text-only mode (no allowed tags), HTML entities that DOMPurify inserted
 * for stray `<` / `>` are decoded back so React renders the literal characters
 * instead of "&lt;" / "&gt;".
 */
const sanitizeString = (value: string, allowedTags: string[] = [], allowedAttr: string[] = []): string => {
	const sanitized = DOMPurify.sanitize(value, {
		ALLOWED_TAGS: allowedTags,
		ALLOWED_ATTR: allowedAttr,
		KEEP_CONTENT: true,
	});

	// Text-only mode: consumer treats output as plain text, decode entities.
	// AllowTags mode: consumer renders as HTML, entities must stay encoded.
	if (allowedTags.length === 0) {
		return decodeHtmlEntities(sanitized);
	}
	return sanitized;
};

/**
 * Decode HTML entities that DOMPurify produces when escaping stray `<` / `>`
 * in plain text (e.g. section names like "SCREENER <<<<<").
 *
 * Safe in text-only mode because:
 *  - DOMPurify has already stripped all tags (ALLOWED_TAGS: [])
 *  - Output is rendered by React as text content, not innerHTML
 *  - Decoded `<` displays literally, never re-parsed as a tag
 *
 * `&amp;` is decoded LAST to avoid double-decoding sequences like `&amp;lt;`.
 */
const decodeHtmlEntities = (value: string): string => {
	return value
		.replace(/&lt;/g, "<")
		.replace(/&gt;/g, ">")
		.replace(/&quot;/g, '"')
		.replace(/&#x27;/g, "'")
		.replace(/&#39;/g, "'")
		.replace(/&#x2F;/g, "/")
		.replace(/&amp;/g, "&");
};

/**
 * Recursively sanitize all string values in response data.
 * Preserves data structure (arrays, objects, primitives).
 *
 * @param data - Response data to sanitize
 * @param currentKey - Current object key (used for partial skip matching)
 * @param skipFields - Set of field names to skip sanitization for
 * @param allowedTags - HTML tags to allow (empty = strip all)
 * @param allowedAttr - HTML attributes to allow
 */
const sanitizeRecursive = <T>(data: T, skipFields?: Set<string>, currentKey?: string, allowedTags?: string[], allowedAttr?: string[]): T => {
	// Null/undefined pass through
	if (data === null || data === undefined) {
		return data;
	}

	// If current key is in skipFields, return raw value
	if (currentKey && skipFields?.has(currentKey)) {
		return data;
	}

	// Arrays: recurse each element
	if (Array.isArray(data)) {
		return data.map((item) => sanitizeRecursive(item, skipFields, undefined, allowedTags, allowedAttr)) as T;
	}

	// Strings: sanitize with DOMPurify
	if (typeof data === "string") {
		return sanitizeString(data, allowedTags, allowedAttr) as T;
	}

	// Primitives: pass through
	if (typeof data === "number" || typeof data === "boolean") {
		return data;
	}

	// Date, File, Blob: pass through (don't sanitize binary/special objects)
	if (data instanceof Date || data instanceof File || data instanceof Blob) {
		return data;
	}

	// Plain objects: recurse each property
	if (typeof data === "object" && data !== null && data.constructor === Object) {
		const sanitized: Record<string, unknown> = {};
		for (const key in data) {
			if (Object.prototype.hasOwnProperty.call(data, key)) {
				sanitized[key] = sanitizeRecursive(
					(data as Record<string, unknown>)[key],
					skipFields,
					key, // Pass key so next level can check skipFields
					allowedTags,
					allowedAttr,
				);
			}
		}
		return sanitized as T;
	}

	// Any other type (Symbol, Function, etc.): pass through
	return data;
};

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Sanitize API response data based on endpoint-specific configuration.
 *
 * @param data - Response data from axios
 * @param url - API endpoint URL (used to look up override config)
 * @returns Sanitized data with same structure
 */
export const sanitizeResponseData = <T>(data: T, url: string): T => {
	const config: ISanitizationConfig | undefined = getSanitizationConfig(url);

	// Mode: Skip — return raw data (for presigned URLs, etc.)
	if (config?.mode === SanitizationModeType.Skip) {
		return data;
	}

	// Mode: Partial — skip specific fields, sanitize everything else
	if (config?.mode === SanitizationModeType.Partial) {
		const skipFields = new Set(config.skipFields);
		return sanitizeRecursive(data, skipFields);
	}

	// Mode: AllowTags — sanitize but preserve specific HTML tags
	if (config?.mode === SanitizationModeType.AllowTags) {
		return sanitizeRecursive(data, undefined, undefined, config.allowedTags, config.allowedAttr);
	}

	// Default: strip ALL HTML tags from all strings
	return sanitizeRecursive(data);
};

/**
 * Sanitize error response messages before they reach the UI.
 * Always uses default (strip all HTML) since error messages should never contain HTML.
 */
export const sanitizeErrorMessage = (message: unknown): string => {
	if (typeof message === "string") {
		return sanitizeString(message);
	}
	if (message !== null && message !== undefined) {
		return sanitizeString(String(message));
	}
	return "";
};

/**
 * Sanitize the entire error response data object.
 * Uses default mode (strip all HTML) since error responses should never contain valid HTML.
 * Handles axios error shape: error.response.data
 */
export const sanitizeErrorResponse = <T>(errorData: T): T => {
	if (errorData === null || errorData === undefined) {
		return errorData;
	}
	// Always strip all HTML from error payloads — no override map needed
	return sanitizeRecursive(errorData);
};

// ─── API Override Map ────────────────────────────────────────────────────────

/**
 * Register API endpoints that need sanitization overrides.
 *
 * Matching rules:
 * - Exact match is checked first
 * - Then prefix match (entries ending with '*')
 * - Keys should be the pathname WITHOUT base URL or query params
 *
 * ⚠️ SECURITY: Every "skip" entry MUST have a `reason` explaining why.
 *   Adding an entry here bypasses XSS protection — review with the team.
 */
const API_SANITIZATION_MAP: Record<string, ISanitizationConfig> = {
	// Presigned URLs contain encoded query params that DOMPurify mangles
	"/files/initiate_upload": {
		mode: SanitizationModeType.Partial,
		skipFields: ["presigned_url", "upload_url", "url", "presigned_urls_info"],
		reason: "S3 presigned URLs contain &, =, % tokens that DOMPurify encodes/strips",
	},
	"/files/get-presigned-url": {
		mode: SanitizationModeType.Partial,
		skipFields: ["presigned_url", "upload_url", "url", "presigned_urls_info"],
		reason: "S3 presigned URLs contain &, =, % tokens that DOMPurify encodes/strips",
	},
	"/quant/initiate_upload": {
		mode: SanitizationModeType.Partial,
		skipFields: ["presigned_url", "upload_url", "url", "presigned_urls_info"],
		reason: "S3 presigned URLs contain &, =, % tokens that DOMPurify encodes/strips",
	},
	"/files/get-download-urls": {
		mode: SanitizationModeType.Partial,
		skipFields: ["presigned_url", "upload_url", "url", "presigned_urls_info"],
		reason: "S3 presigned URLs in nested presigned_urls_info contain &, =, % tokens that DOMPurify encodes/strips",
	},
	"/taxonomy/initiate_upload": {
		mode: SanitizationModeType.Partial,
		skipFields: ["presigned_url", "upload_url", "url", "presigned_urls_info"],
		reason: "S3 presigned URLs contain &, =, % tokens that DOMPurify encodes/strips",
	},
	"/search/widget/export": {
		mode: SanitizationModeType.Partial,
		skipFields: ["url"],
		reason: "S3 presigned download URL (qual + notes counts widget export) — DOMPurify mangles &, =, % tokens",
	},

	// Persona chat: groundingMarkup contains <<GR>>...</GR>> tags that DOMPurify strips as malformed HTML.
	// These tags are never rendered as raw HTML — they're parsed by parseGroundedText() into React elements.
	"/persona/chat/generate-inline": {
		mode: SanitizationModeType.Partial,
		skipFields: ["grounding_markup"],
		reason: "grounding_markup uses <<GR>>/<</GR>> marker tags parsed by GroundedText component, not rendered as HTML",
	},
	"/persona/chat/generate": {
		mode: SanitizationModeType.Partial,
		skipFields: ["grounding_markup"],
		reason: "grounding_markup uses <<GR>>/<</GR>> marker tags parsed by GroundedText component, not rendered as HTML",
	},
	"/persona/persona-projects": {
		mode: SanitizationModeType.Partial,
		skipFields: ["groundingMarkup"],
		reason: "Persisted groundingMarkup on chat messages uses <<GR>>/<</GR>> marker tags for GroundedText rendering",
	},
};
